/**
 * Inbox types (SPEC E1c — unified-inbox, R4).
 */

/** A per-channel summary on an inbox contact row (GET /api/inbox). */
export interface InboxChannel {
  channel: string;
  conversation_id: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  status: string | null;
  unread_count: number;
  assignee_id: string | null;
}

/** A contact row in the inbox list (conversation-gated → channels non-empty). */
export interface InboxContact {
  contact_id: string;
  display_name: string;
  company_name?: string | null;
  channels: InboxChannel[];
  last_active_at: string | null;
  unread_total: number;
}

export interface InboxListResponse {
  contacts: InboxContact[];
  has_more: boolean;
  next_cursor: string | null;
}

/** A conversation thread summary from GET /api/contacts/{id}/conversations. */
export interface ContactConversation {
  id: string;
  channel: string | null;
  last_message_at: string | null;
  status: string | null;
  last_message_preview: string | null;
  unread_count: number;
  assignee_id: string | null;
  snoozed_until: string | null;
}

export interface ContactConversationsResponse {
  conversations: ContactConversation[];
  count: number;
}

/** A single message in a conversation thread (GET /api/conversations/{id}). */
export interface InboxMessage {
  id: string;
  direction: 'inbound' | 'outbound' | 'note';
  content: string;
  sender_handle?: string;
  sender_display_name?: string;
  external_message_id?: string;
  received_at?: string | null;
  created_at?: string | null;
  metadata?: Record<string, unknown>;
  ai_draft_id?: string | null;
}

/** A conversation doc with its recent messages (GET /api/conversations/{id}). */
export interface ConversationDetail {
  id: string;
  tenant_id?: string;
  contact_id?: string;
  prospect_id?: string;
  channel: string;
  status?: string;
  last_message_at?: string | null;
  last_inbound_at?: string | null;
  standby_state?: boolean;
  unread_count?: number;
  assignee_id?: string | null;
  messages: InboxMessage[];
}

/** Contact-context panel data (GET /api/contacts/{id}). */
export interface ContactDetail {
  id: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  company_name?: string;
  job_title?: string;
  phone?: string;
  lifecycle_status?: string;
  channels?: { channel_type: string; channel_value: string; channel_value_display?: string }[];
}

/** An open opportunity in the context panel (GET /api/contacts/{id}/opportunities). */
export interface ContactOpportunity {
  id: string;
  stage_key: string;
  pipeline_id?: string;
  value?: number;
  currency?: string;
  name?: string;
  stage_changed_at?: string;
}

/** A pending AI draft surfaced inline (GET /api/ai-drafts?status=pending&conversation_id). */
export interface PendingDraft {
  id: string;
  conversation_id: string;
  draft_content: string;
  classification?: string;
  status: string;
}

/** Channels whose send adapter is ACTIVE (operator can type + Send). Email +
 * WhatsApp are stubs (501) until G4 / E1a land. */
export const SENDABLE_CHANNELS = new Set([
  'messenger',
  'sms',
  'twitter_dm',
  'twitter_reply',
  'twitter_mention',
]);
